import React, { useState } from "react";
import { X, Send, Loader, CheckCircle2, Link as LinkIcon, Copy, Mail } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

const ORANGE = "#FF6A00";
const NAVY = "#0F1B2D";

/**
 * EmailReportModal — reusable "Email this report" modal mounted from the
 * Strategy Brief and Interview Playbook result pages. Generates a public
 * read-only share link (idempotent), lets the user copy it, and optionally
 * emails it via Resend with a personal note.
 *
 * Props:
 *   - open:        boolean
 *   - onClose:     () => void
 *   - kindLabel:   "Strategy Brief" | "Interview Playbook"
 *   - sharePath:   POST endpoint for ensure_share_token (e.g. "/strategy-brief/<id>/share")
 *   - emailPath:   POST endpoint for sending the email (e.g. "/strategy-brief/<id>/email")
 *   - publicPath:  public client-side path prefix (e.g. "/strategy-brief/share/")
 *
 * Backend (see /app/backend/share_reports.py):
 *   - POST <sharePath>           → { share_token, public_url }
 *   - POST <emailPath> {recipient_email, recipient_name?, note?}
 *                                 → { ok, recipient_email, share_token }
 *   - 429 if user has already emailed this recipient 10 times in 24h
 */
export default function EmailReportModal({
  open, onClose, kindLabel, sharePath, emailPath, publicPath,
}) {
  const [token, setToken] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [copyOk, setCopyOk] = useState(false);

  const publicUrl = token
    ? `${window.location.origin}${publicPath}${token}`
    : "";

  const ensureToken = async () => {
    if (token) return token;
    setError("");
    try {
      const { data } = await api.post(sharePath);
      setToken(data.share_token);
      return data.share_token;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't create share link.");
      throw e;
    }
  };

  const handleCopy = async () => {
    setError("");
    try {
      const t = await ensureToken();
      const url = `${window.location.origin}${publicPath}${t}`;
      await navigator.clipboard.writeText(url);
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 1500);
    } catch { /* error already surfaced */ }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setError("");
    if (!recipientEmail || !recipientEmail.includes("@")) {
      setError("Need a valid email address.");
      return;
    }
    setBusy(true);
    try {
      await ensureToken();
      await api.post(emailPath, {
        recipient_email: recipientEmail,
        recipient_name: recipientName || null,
        note: note || null,
      });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't send email — try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15, 27, 45, 0.6)" }}
      onClick={onClose}
      data-testid="email-report-modal"
    >
      <div
        className="rm-card-static p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        style={{ background: "white", borderColor: NAVY, boxShadow: `5px 5px 0 0 ${ORANGE}` }}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="font-mono-rm uppercase text-[10px] tracking-widest"
                 style={{ color: ORANGE, fontWeight: 700 }}>
              · Email this {kindLabel.toLowerCase()}
            </div>
            <h2 className="font-display uppercase text-xl leading-tight mt-1"
                style={{ color: NAVY, letterSpacing: "-0.02em" }}
                data-testid="email-report-title">
              {sent ? "Sent." : "Share with a coach or friend."}
            </h2>
            {!sent && (
              <p className="text-[12px] mt-1" style={{ color: "#475569" }}>
                Read-only public link, valid until you revoke it.
              </p>
            )}
          </div>
          <button onClick={onClose}
                  className="rm-btn rm-btn-ghost text-xs px-2 py-2"
                  data-testid="email-report-close">
            <X size={14} />
          </button>
        </div>

        {sent ? (
          <div className="rm-card-static p-4 text-center"
               style={{ background: "#D1FAE5", borderColor: "#0E5A4A" }}
               data-testid="email-report-success">
            <CheckCircle2 size={32} style={{ color: "#0E5A4A", margin: "0 auto" }} />
            <p className="text-sm mt-2" style={{ color: "#064E3B" }}>
              <strong>{recipientEmail}</strong> just got the read-only link in their inbox.
            </p>
            {publicUrl && (
              <button onClick={handleCopy}
                      className="rm-btn rm-btn-ghost text-xs px-2 py-1 mt-3 inline-flex items-center gap-1"
                      data-testid="email-report-copy-after-send">
                <Copy size={11} /> {copyOk ? "Copied!" : "Copy link too"}
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Share-link section */}
            <div className="rm-card-static p-3 mb-3"
                 style={{ background: "#FFF7F2", borderColor: "#FED7AA" }}
                 data-testid="email-report-link-block">
              <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-1.5"
                   style={{ color: ORANGE, fontWeight: 700 }}>
                · Public read-only link
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0 font-mono text-[11px] truncate p-2 rounded"
                     style={{ background: "white", border: "1px solid #E2E8F0", color: NAVY }}
                     data-testid="email-report-link-display">
                  {publicUrl || "Generating on first share…"}
                </div>
                <button onClick={handleCopy}
                        className="rm-btn rm-btn-primary text-xs px-2.5 py-2 inline-flex items-center gap-1"
                        data-testid="email-report-copy-btn">
                  {copyOk ? <CheckCircle2 size={12} /> : <Copy size={12} />}
                  {copyOk ? "Copied!" : "Copy"}
                </button>
              </div>
            </div>

            <form onSubmit={handleSend} className="space-y-3"
                  data-testid="email-report-form">
              <div>
                <label className="rm-label text-[11px]">Recipient email *</label>
                <input type="email" value={recipientEmail}
                       onChange={(e) => setRecipientEmail(e.target.value)}
                       className="rm-input w-full"
                       placeholder="coach@example.com"
                       data-testid="email-report-recipient" required />
              </div>
              <div>
                <label className="rm-label text-[11px]">Their name (optional)</label>
                <input value={recipientName}
                       onChange={(e) => setRecipientName(e.target.value)}
                       className="rm-input w-full"
                       placeholder="Alex"
                       data-testid="email-report-name" />
              </div>
              <div>
                <label className="rm-label text-[11px]">Note (optional)</label>
                <textarea value={note}
                          onChange={(e) => setNote(e.target.value)}
                          rows={3} className="rm-input w-full"
                          placeholder="Wanted your take on this — particularly the talking points."
                          maxLength={600}
                          data-testid="email-report-note" />
                <div className="text-[10px] text-right mt-0.5" style={{ color: "#94A3B8" }}>
                  {note.length}/600
                </div>
              </div>

              {error && (
                <div className="rm-card-static p-2 text-xs font-mono-rm"
                     style={{ background: "#FEE2E2", borderColor: "#B91C1C", color: "#991B1B" }}
                     data-testid="email-report-error">
                  · {error}
                </div>
              )}

              <div className="flex justify-end gap-2 flex-wrap">
                <button type="button" onClick={onClose}
                        className="rm-btn rm-btn-ghost text-xs px-3 py-2"
                        data-testid="email-report-cancel">
                  Cancel
                </button>
                <button type="submit" disabled={busy}
                        className="rm-btn rm-btn-primary text-sm px-4 py-2"
                        data-testid="email-report-send-btn">
                  {busy ? <Loader size={14} className="animate-spin" /> : <Mail size={14} />}
                  {busy ? "Sending…" : "Send email"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
